import type { WorkEntryRow } from '../types';

export type WorkEntryDateRangeSource = {
    dateRange?: { start?: string | null; end?: string | null } | null;
};

export function formatWorkEntryYearRange(source: WorkEntryDateRangeSource | null | undefined): string {
    if (!source) return '';
    return formatYearRangeFromDates(source.dateRange?.start, source.dateRange?.end);
}

/** Sort work entries by `dateRange.end` (latest first). */
export function getWorkEntryEndSortTimestamp(source: WorkEntryDateRangeSource | null | undefined): number {
    return getEndSortTimestampFromDate(source?.dateRange?.end);
}

export function getEndSortTimestampFromDate(end?: string | null): number {
    if (!end) return 0;
    const timestamp = new Date(end).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
}

export function toYear(value?: string | null): number | undefined {
    if (!value) return undefined;
    const parsed = Number(value.slice(0, 4));
    return Number.isFinite(parsed) ? parsed : undefined;
}

export function getYearsFromDateRange(start?: string | null, end?: string | null): number[] {
    const startYear = toYear(start);
    const endYear = toYear(end);

    if (startYear == null && endYear == null) return [];
    if (startYear != null && endYear != null) {
        const min = Math.min(startYear, endYear);
        const max = Math.max(startYear, endYear);
        const years: number[] = [];
        for (let year = min; year <= max; year += 1) {
            years.push(year);
        }
        return years;
    }

    return [startYear ?? endYear!];
}

export function formatYearRangeFromDates(start?: string | null, end?: string | null): string {
    const years = getYearsFromDateRange(start, end);
    if (!years.length) return '';
    const rangeStart = years[0];
    const rangeEnd = years[years.length - 1];
    return rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`;
}

export function parseRoleLabels(professionalRoles?: Array<string | null> | null): string[] {
    const roles: string[] = [];
    const roleValues = (professionalRoles?.filter(Boolean) as string[]) ?? [];
    for (const role of roleValues) {
        for (const part of role.split(',')) {
            const trimmed = part.trim();
            if (trimmed) roles.push(trimmed);
        }
    }
    return roles;
}

export function buildRowsWithStableIds<TSource>(
    items: ReadonlyArray<TSource>,
    getBaseId: (item: TSource) => string,
    toRow: (item: TSource, id: string) => WorkEntryRow<TSource>
): WorkEntryRow<TSource>[] {
    const occurrences = new Map<string, number>();
    return items.map((item) => {
        const base = getBaseId(item);
        const occurrence = occurrences.get(base) ?? 0;
        occurrences.set(base, occurrence + 1);
        const id = occurrence === 0 ? base : `${base}|${occurrence}`;
        return toRow(item, id);
    });
}
